/**
 * Reports Page - Generazione Report PDF
 * Il Diario di Bordo - Frontend
 * 
 * Wizard multi-step per generazione report:
 * 1. Selezione Template
 * 2. Selezione Target e Periodo
 * 3. Compilazione Testi (5 categorie per Report Completo)
 * 4. Anteprima e Download
 * 
 * I grafici vengono generati nel browser con Chart.js e inviati al backend come base64
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { reportsApi } from '../../services/api'
import { useChartGenerator } from '../../hooks/useChartGenerator'
import { Card } from '../../components/ui'
import { 
  FileText, Download, Eye, ChevronRight, ChevronLeft, 
  Calendar, User, Settings, Loader2, Star, Copy, Plus,
  CheckCircle, AlertCircle, FileSpreadsheet, Users, Edit
} from 'lucide-react'

// === COSTANTI CATEGORIE NOTE ===
const NOTE_CATEGORIES = [
  {
    id: 'descrizioneGiornata',
    label: 'Descrizione Giornata',
    color: '#2563eb',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700 dark:text-blue-400',
    sourceField: 'descrizioneGiornata',
    placeholder: 'Sintesi delle attività svolte, comportamento generale ed eventi significativi...'
  },
  {
    id: 'interventiEducativi',
    label: 'Interventi Educativi',
    color: '#22c55e',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-500',
    textColor: 'text-green-700 dark:text-green-400',
    sourceField: 'interventiEducativi',
    placeholder: 'Interventi attuati, strategie utilizzate e risultati osservati...'
  },
  {
    id: 'situazioneFamiliare',
    label: 'Situazione Familiare',
    color: '#f59e0b',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-700 dark:text-amber-400',
    sourceField: 'situazioneFamiliare',
    placeholder: 'Aggiornamenti sulla situazione familiare, comunicazioni con i genitori...'
  },
  {
    id: 'annotazioniGenitore',
    label: 'Annotazioni Genitore',
    color: '#8b5cf6',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    borderColor: 'border-purple-500',
    textColor: 'text-purple-700 dark:text-purple-400',
    sourceField: 'annotazioniGenitore',
    placeholder: 'Comunicazioni, richieste o segnalazioni ricevute dai genitori/tutori...'
  },
  {
    id: 'urgenzaAttiva',
    label: 'Urgenza Attiva',
    color: '#ef4444',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-500',
    textColor: 'text-red-700 dark:text-red-400',
    sourceField: 'dettaglioUrgenza',
    placeholder: 'Situazioni di urgenza in corso, azioni intraprese e follow-up necessari...'
  }
]

// === STEP COMPONENTS ===

/**
 * Step 1: Selezione Template
 */
function TemplateSelectionStep({ templates, selectedTemplate, onSelect, onCreateNew, canCreate, onDuplicate, onEdit }) {
  const typeIcons = {
    minore_completo: <FileText className="w-6 h-6" />,
    minore_sintetico: <FileSpreadsheet className="w-6 h-6" />,
    presenze_centro: <Users className="w-6 h-6" />,
    custom: <Settings className="w-6 h-6" />
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Seleziona il tipo di report
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Scegli un template predefinito o uno dei tuoi template personalizzati
        </p>
      </div>
      
      {/* Template di Sistema */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          Template di Sistema
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templates.system?.map(template => (
            <button
              key={template.id}
              onClick={() => onSelect(template)}
              className={`
                p-4 rounded-xl border-2 text-left transition-all
                ${selectedTemplate?.id === template.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                }
              `}
            >
              <div className="flex items-start gap-3">
                <div className={`
                  p-2 rounded-lg
                  ${selectedTemplate?.id === template.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }
                `}>
                  {typeIcons[template.type] || <FileText className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-medium text-gray-900 dark:text-white truncate">
                    {template.name}
                  </h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {template.description}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded">
                      Sistema
                    </span>
                    <span className="text-xs text-gray-400">
                      {template.enabledSectionsCount} sezioni
                    </span>
                    {canCreate && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onDuplicate(template.id)
                        }}
                        className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
                        title="Duplica template"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Template Custom */}
      {templates.custom?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4 text-blue-500" />
            I tuoi template personalizzati
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {templates.custom.map(template => (
              <button
                key={template.id}
                onClick={() => onSelect(template)}
                className={`
                  p-4 rounded-xl border-2 text-left transition-all relative group
                  ${selectedTemplate?.id === template.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div className={`
                    p-2 rounded-lg
                    ${selectedTemplate?.id === template.id
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }
                  `}>
                    <Settings className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-gray-900 dark:text-white truncate">
                      {template.name}
                    </h5>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {template.description || 'Template personalizzato'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded">
                        Custom
                      </span>
                      <span className="text-xs text-gray-400">
                        {template.enabledSectionsCount} sezioni
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(template.id)
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm
                             opacity-0 group-hover:opacity-100 transition-opacity
                             hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Modifica template"
                >
                  <Edit className="w-4 h-4 text-gray-500" />
                </button>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Crea nuovo template */}
      {canCreate && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
          >
            <Plus className="w-5 h-5" />
            Crea nuovo template personalizzato
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Step 2: Selezione Target e Periodo
 */
function TargetSelectionStep({ template, minori, selectedMinore, onSelectMinore, dateRange, onDateChange }) {
  const isMinoreReport = template?.targetType === 'minore'
  
  const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate()
  
  // Formatta data come YYYY-MM-DD usando ora LOCALE (non UTC!)
  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  const periodPresets = [
    { 
      label: 'Mese corrente', 
      getValue: () => {
        const now = new Date()
        const year = now.getFullYear()
        const month = now.getMonth()
        const lastDay = getLastDayOfMonth(year, month)
        return { start: new Date(year, month, 1), end: new Date(year, month, lastDay) }
      }
    },
    { 
      label: 'Mese precedente', 
      getValue: () => {
        const now = new Date()
        const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
        const month = now.getMonth() === 0 ? 11 : now.getMonth() - 1
        const lastDay = getLastDayOfMonth(year, month)
        return { start: new Date(year, month, 1), end: new Date(year, month, lastDay) }
      }
    },
    { 
      label: 'Ultimo trimestre', 
      getValue: () => {
        const now = new Date()
        let endYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
        let endMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1
        let startYear = endMonth < 2 ? endYear - 1 : endYear
        let startMonth = endMonth < 2 ? 12 + (endMonth - 2) : endMonth - 2
        const lastDay = getLastDayOfMonth(endYear, endMonth)
        return { start: new Date(startYear, startMonth, 1), end: new Date(endYear, endMonth, lastDay) }
      }
    }
  ]
  
  const applyPreset = (preset) => {
    const { start, end } = preset.getValue()
    onDateChange({ startDate: formatDate(start), endDate: formatDate(end) })
  }
  
  const formatPeriodDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return null
    const start = new Date(dateRange.startDate)
    const end = new Date(dateRange.endDate)
    return `${start.toLocaleDateString('it-IT')} - ${end.toLocaleDateString('it-IT')}`
  }
  
  return (
    <div className="space-y-6">
      {isMinoreReport && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Seleziona il minore</h3>
          <div className="relative">
            <select
              value={selectedMinore || ''}
              onChange={(e) => onSelectMinore(e.target.value)}
              className="w-full p-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Seleziona un minore --</option>
              {minori.map(minore => (
                <option key={minore._id} value={minore._id}>{minore.nomeCompleto}</option>
              ))}
            </select>
            <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>
      )}
      
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Periodo del report</h3>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {periodPresets.map(preset => (
            <button
              key={preset.label}
              onClick={() => applyPreset(preset)}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded-full
                         hover:bg-blue-100 dark:hover:bg-blue-900/30 
                         text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data inizio</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => onDateChange({ ...dateRange, startDate: e.target.value })}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data fine</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => onDateChange({ ...dateRange, endDate: e.target.value })}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {formatPeriodDisplay() && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Periodo selezionato:</span>
              <span className="text-sm">{formatPeriodDisplay()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Step 3: Compilazione Testi
 * AGGIORNATO: Mostra sezioni in base al template selezionato
 */
function TextCompilationStep({ compilationData, textInputs, onTextChange, template }) {
  if (!compilationData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }
  
  const { testiOriginali, presenze, comportamenti, customSections } = compilationData
  
  // Determina quali sezioni mostrare in base al template
  const hasNoteCategorie = template?.sections?.some(s => s.id === 'note_categorie' && s.enabled)
  const hasTestoLibero = template?.sections?.some(s => s.id === 'testo_libero' && s.enabled)
  const hasAttivitaCustom = template?.sections?.some(s => s.id === 'attivita_custom' && s.enabled)
  const hasSintesi = template?.sections?.some(s => s.id === 'sintesi' && s.enabled)
  
  // Note/Osservazioni: solo se template ha note_categorie o testo_libero (solo Report Completo)
  const showNotes = hasNoteCategorie || hasTestoLibero
  // Usa le 5 categorie solo per note_categorie
  const useCategories = hasNoteCategorie
  
  const renderCategoryField = (category) => {
    const sourceData = testiOriginali?.[category.sourceField] || []
    const inputKey = `note_${category.id}`
    
    return (
      <div key={category.id} className={`rounded-lg border-l-4 ${category.borderColor} bg-white dark:bg-gray-800 shadow-sm`}>
        <div className={`p-4 ${category.bgColor} rounded-t-lg border-b border-gray-200 dark:border-gray-700`}>
          <h4 className={`font-semibold ${category.textColor}`}>{category.label}</h4>
        </div>
        
        <div className="p-4 space-y-3">
          {sourceData.length > 0 ? (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Dati registrati nel periodo ({sourceData.length})
                </span>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-2">
                {sourceData.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="text-sm border-l-2 border-gray-300 dark:border-gray-600 pl-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                      {new Date(item.date).toLocaleDateString('it-IT')}:
                    </span>
                    <p className="text-gray-700 dark:text-gray-300 mt-0.5">{item.text}</p>
                  </div>
                ))}
                {sourceData.length > 10 && (
                  <p className="text-xs text-gray-500 italic">... e altri {sourceData.length - 10} record</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">Nessun dato registrato per questa categoria nel periodo</span>
            </div>
          )}
          
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sintesi per il report:</label>
            <textarea
              value={textInputs[inputKey] || ''}
              onChange={(e) => onTextChange({ ...textInputs, [inputKey]: e.target.value })}
              placeholder={category.placeholder}
              rows={3}
              className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
            />
          </div>
        </div>
      </div>
    )
  }
  
  // Render sezioni custom (Alimentazione, Sonno, etc.)
  const renderCustomSection = (section) => {
    const inputKey = `custom_${section.id}`
    
    // Determina il tipo di sezione dai dati (più robusto che affidarsi a section.type)
    const hasMultiselectData = section.data && section.data.length > 0 && 
      section.data[0].hasOwnProperty('label') && section.data[0].hasOwnProperty('count')
    const hasNotesData = section.data && section.data.length > 0 && 
      section.data[0].hasOwnProperty('date') && section.data[0].hasOwnProperty('text')
    
    return (
      <div key={section.id} className="rounded-lg border-l-4 border-indigo-500 bg-white dark:bg-gray-800 shadow-sm">
        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-t-lg border-b border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-indigo-700 dark:text-indigo-400">{section.name}</h4>
        </div>
        
        <div className="p-4 space-y-3">
          {/* Mostra dati storici per sezioni multiselect */}
          {hasMultiselectData && (
            <details className="bg-gray-50 dark:bg-gray-900/50 rounded-lg" open>
              <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                Dati registrati nel periodo ({section.data.reduce((sum, item) => sum + item.count, 0)} voci)
              </summary>
              <div className="px-3 pb-3 pt-1">
                <div className="flex flex-wrap gap-2">
                  {section.data.slice(0, 10).map((item, idx) => (
                    <span 
                      key={idx} 
                      className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300"
                    >
                      {item.label}: <strong className="ml-1">{item.count}x</strong>
                    </span>
                  ))}
                </div>
              </div>
            </details>
          )}
          
          {/* Mostra dati storici per sezioni note */}
          {hasNotesData && (
            <details className="bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <summary className="px-3 py-2 cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                Note registrate ({section.data.length})
              </summary>
              <div className="px-3 pb-3 pt-1 max-h-32 overflow-y-auto space-y-2">
                {section.data.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-sm border-l-2 border-indigo-300 dark:border-indigo-600 pl-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                      {new Date(item.date).toLocaleDateString('it-IT')}:
                    </span>
                    <p className="text-gray-700 dark:text-gray-300 mt-0.5">{item.text}</p>
                  </div>
                ))}
              </div>
            </details>
          )}
          
          {/* Campo per sintesi */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sintesi per il report:</label>
            <textarea
              value={textInputs[inputKey] || ''}
              onChange={(e) => onTextChange({ ...textInputs, [inputKey]: e.target.value })}
              placeholder={`Scrivi la tua sintesi per ${section.name}...`}
              rows={3}
              className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
            />
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Compila i campi del report</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {useCategories ? 'Rivedi i dati registrati per ogni categoria e scrivi le tue sintesi' : 'Rivedi i dati registrati e scrivi le tue note'}
        </p>
      </div>
      
      {presenze && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Statistiche del periodo</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-blue-600 dark:text-blue-400">Presenze:</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                {presenze.presenti}/{presenze.totaleGiorni} ({presenze.percentuale}%)
              </span>
            </div>
            {presenze.trend && (
              <div>
                <span className="text-blue-600 dark:text-blue-400">Trend:</span>
                <span className={`ml-2 font-semibold ${presenze.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                  {presenze.trend}
                </span>
              </div>
            )}
            {comportamenti?.current && (
              <div>
                <span className="text-blue-600 dark:text-blue-400">Positivi:</span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">{comportamenti.current.positive}%</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Note e Osservazioni - Solo per Report Completo */}
      {showNotes && (useCategories ? (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Note e Osservazioni per Categoria
          </h4>
          <div className="space-y-4">{NOTE_CATEGORIES.map(category => renderCategoryField(category))}</div>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Note e Osservazioni</label>
          {testiOriginali?.descrizioneGiornata?.length > 0 && (
            <details className="mb-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <summary className="px-4 py-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
                Vedi note registrate ({testiOriginali.descrizioneGiornata.length})
              </summary>
              <div className="px-4 pb-3 max-h-48 overflow-y-auto">
                {testiOriginali.descrizioneGiornata.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                    <span className="text-xs text-gray-500">{new Date(item.date).toLocaleDateString('it-IT')}:</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </details>
          )}
          <textarea
            value={textInputs.note || ''}
            onChange={(e) => onTextChange({ ...textInputs, note: e.target.value })}
            placeholder="Scrivi qui il tuo riassunto delle note e osservazioni..."
            rows={5}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
          />
        </div>
      ))}
      
      {/* Sezioni Custom (es. Alimentazione, Sonno) - Solo per Report Completo */}
      {hasAttivitaCustom && customSections && customSections.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-500" />
            Sezioni Personalizzate
          </h4>
          <div className="space-y-4">
            {customSections.map(section => renderCustomSection(section))}
          </div>
        </div>
      )}
      
      {/* Sintesi Valutativa - Per tutti i template che hanno la sezione sintesi */}
      {hasSintesi && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sintesi Valutativa</label>
          <textarea
            value={textInputs.sintesi || ''}
            onChange={(e) => onTextChange({ ...textInputs, sintesi: e.target.value })}
            placeholder="Scrivi la tua valutazione complessiva del periodo..."
            rows={5}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
          />
        </div>
      )}
      
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <p className="text-sm text-amber-800 dark:text-amber-300">
          <strong>Nota:</strong> I campi lasciati vuoti non appariranno nel PDF finale.
        </p>
      </div>
    </div>
  )
}

/**
 * Step 4: Anteprima e Download
 */
function PreviewStep({ previewHtml, isGenerating, onDownload, saveToGoogleDrive, onToggleGoogleDrive, googleDriveConfigured, generatingCharts }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Anteprima Report</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Verifica il contenuto prima di scaricare</p>
        </div>
        
        <div className="flex items-center gap-4">
          {googleDriveConfigured && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saveToGoogleDrive}
                onChange={(e) => onToggleGoogleDrive(e.target.checked)}
                className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Salva anche su Google Drive</span>
            </label>
          )}
          
          <button
            onClick={onDownload}
            disabled={isGenerating || generatingCharts}
            className="flex items-center gap-2 px-6 py-2.5 bg-green-500 text-white rounded-lg
                       hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {generatingCharts ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generazione grafici...
              </>
            ) : isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generazione PDF...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Scarica PDF
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white">
        {previewHtml ? (
          <iframe srcDoc={previewHtml} className="w-full h-[600px]" title="Anteprima Report" />
        ) : (
          <div className="flex items-center justify-center h-[400px] bg-gray-50 dark:bg-gray-800">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">Generazione anteprima...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// === MAIN COMPONENT ===

export default function ReportsPage() {
  const { user, tenant } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()
  const { generateAllCharts } = useChartGenerator()
  
  // State
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState({ system: [], custom: [], canCreateMore: false })
  const [minori, setMinori] = useState([])
  
  // Wizard state
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selectedMinore, setSelectedMinore] = useState(null)
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth()
    const lastDay = new Date(year, month + 1, 0).getDate()
    const start = new Date(year, month, 1)
    const end = new Date(year, month, lastDay)
    
    // Formatta come YYYY-MM-DD usando ora LOCALE (non UTC!)
    const formatLocalDate = (date) => {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
    
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) }
  })
  const [textInputs, setTextInputs] = useState({})
  const [compilationData, setCompilationData] = useState(null)
  const [previewHtml, setPreviewHtml] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatingCharts, setGeneratingCharts] = useState(false)
  const [saveToGoogleDrive, setSaveToGoogleDrive] = useState(false)
  
  // Ref per textInputs - evita closure stale in loadPreview
  const textInputsRef = useRef(textInputs)
  useEffect(() => {
    textInputsRef.current = textInputs
  }, [textInputs])
  
  // Load initial data
  useEffect(() => {
    loadData()
  }, [])
  
  const loadData = async () => {
    setLoading(true)
    try {
      const [templatesRes, minoriRes] = await Promise.all([
        reportsApi.getTemplates(),
        reportsApi.getMinoriList()
      ])
      setTemplates(templatesRes.data?.data || { system: [], custom: [] })
      setMinori(minoriRes.data?.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
      showError('Errore nel caricamento dati')
    } finally {
      setLoading(false)
    }
  }
  
  const loadCompilationData = useCallback(async () => {
    if (!selectedTemplate || !dateRange.startDate || !dateRange.endDate) return
    
    const targetType = selectedTemplate.targetType || 'minore'
    const targetId = targetType === 'minore' ? selectedMinore : tenant?.id
    
    if (targetType === 'minore' && !selectedMinore) return
    
    try {
      const response = await reportsApi.getCompilationData(targetType, targetId, dateRange.startDate, dateRange.endDate)
      setCompilationData(response.data?.data)
    } catch (error) {
      console.error('Error loading compilation data:', error)
      showError('Errore nel caricamento dati')
    }
  }, [selectedTemplate, selectedMinore, dateRange, tenant, showError])
  
  const loadPreview = useCallback(async () => {
    if (!selectedTemplate) return
    
    const targetType = selectedTemplate.targetType || 'minore'
    const targetId = targetType === 'minore' ? selectedMinore : tenant?.id
    
    try {
      const response = await reportsApi.generatePreview({
        templateId: selectedTemplate.id,
        targetType,
        targetId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        textInputs: textInputsRef.current  // Usa ref per valore sempre aggiornato
      })
      setPreviewHtml(response.data?.data?.html)
    } catch (error) {
      console.error('Error loading preview:', error)
      showError('Errore nella generazione anteprima')
    }
  }, [selectedTemplate, selectedMinore, dateRange, tenant, showError])
  // textInputsRef è un ref, non cambia mai - evita closure stale
  
  // Carica dati solo quando si ENTRA in uno step (non quando cambiano dipendenze)
  const prevStepRef = useRef(step)
  
  useEffect(() => {
    const prevStep = prevStepRef.current
    prevStepRef.current = step
    
    // Carica solo quando si ENTRA nello step (non quando le dipendenze cambiano)
    if (step === 3 && prevStep !== 3) {
      loadCompilationData()
    } else if (step === 4 && prevStep !== 4) {
      loadPreview()
    }
  }, [step, loadCompilationData, loadPreview])
  
  const canProceed = () => {
    switch (step) {
      case 1: return !!selectedTemplate
      case 2: 
        if (selectedTemplate?.targetType === 'minore') {
          return !!selectedMinore && !!dateRange.startDate && !!dateRange.endDate
        }
        return !!dateRange.startDate && !!dateRange.endDate
      case 3: return true
      case 4: return true
      default: return false
    }
  }
  
  const goNext = () => { if (canProceed() && step < 4) setStep(step + 1) }
  const goBack = () => { if (step > 1) setStep(step - 1) }
  
  // Download PDF con generazione grafici frontend
  const handleDownload = async () => {
    const targetType = selectedTemplate.targetType || 'minore'
    const targetId = targetType === 'minore' ? selectedMinore : tenant?.id
    
    // Determina tipo grafico comportamenti dal template
    const behaviorSection = selectedTemplate.sections?.find(s => s.id === 'comportamenti_grafico')
    const behaviorChartType = behaviorSection?.options?.chartType || 'line'
    
    try {
      // 1. Genera grafici nel browser
      setGeneratingCharts(true)
      let charts = {}
      
      if (compilationData) {
        charts = await generateAllCharts(compilationData, { behaviorChartType })
      }
      
      setGeneratingCharts(false)
      setIsGenerating(true)
      
      // 2. Invia richiesta al backend con grafici base64
      const response = await reportsApi.generatePdf({
        templateId: selectedTemplate.id,
        targetType,
        targetId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        textInputs,
        charts, // Grafici generati dal frontend
        saveToGoogleDrive
      })
      
      // 3. Download PDF
      const contentDisposition = response.headers?.['content-disposition']
      let filename = 'report.pdf'
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/)
        if (match) filename = match[1]
      }
      
      reportsApi.downloadBlob(response.data, filename)
      showSuccess('Report generato con successo!')
      
      if (saveToGoogleDrive) {
        showSuccess('Report salvato anche su Google Drive')
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      showError('Errore nella generazione del PDF')
    } finally {
      setGeneratingCharts(false)
      setIsGenerating(false)
    }
  }
  
  const steps = [
    { num: 1, label: 'Template' },
    { num: 2, label: 'Periodo' },
    { num: 3, label: 'Compilazione' },
    { num: 4, label: 'Anteprima' }
  ]
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }
  
  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Genera Report</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Crea report PDF professionali per i servizi sociali o i genitori</p>
      </div>
      
      {/* Step Indicators */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full font-semibold
                ${step >= s.num ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}
                ${step === s.num ? 'ring-4 ring-blue-200 dark:ring-blue-900' : ''}
              `}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:inline
                ${step >= s.num ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}
              `}>{s.label}</span>
              {idx < steps.length - 1 && (
                <div className={`w-12 sm:w-24 h-1 mx-2 rounded ${step > s.num ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
            </div>
          ))}
        </div>
      </div>
      
      <Card className="p-6">
        {step === 1 && (
          <TemplateSelectionStep
            templates={templates}
            selectedTemplate={selectedTemplate}
            onSelect={setSelectedTemplate}
            onCreateNew={() => navigate('/reports/template/new')}
            onDuplicate={(templateId) => navigate(`/reports/template/new?from=${templateId}`)}
            onEdit={(templateId) => navigate(`/reports/template/${templateId}`)}
            canCreate={templates.canCreateMore && user?.role === 'coordinatore'}
          />
        )}
        
        {step === 2 && (
          <TargetSelectionStep
            template={selectedTemplate}
            minori={minori}
            selectedMinore={selectedMinore}
            onSelectMinore={setSelectedMinore}
            dateRange={dateRange}
            onDateChange={setDateRange}
          />
        )}
        
        {step === 3 && (
          <TextCompilationStep
            compilationData={compilationData}
            textInputs={textInputs}
            onTextChange={setTextInputs}
            template={selectedTemplate}
          />
        )}
        
        {step === 4 && (
          <PreviewStep
            previewHtml={previewHtml}
            isGenerating={isGenerating}
            generatingCharts={generatingCharts}
            onDownload={handleDownload}
            saveToGoogleDrive={saveToGoogleDrive}
            onToggleGoogleDrive={setSaveToGoogleDrive}
            googleDriveConfigured={tenant?.settings?.googleDrive?.configured}
          />
        )}
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={goBack}
            disabled={step === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
              ${step === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}
            `}
          >
            <ChevronLeft className="w-5 h-5" />
            Indietro
          </button>
          
          {step < 4 ? (
            <button
              onClick={goNext}
              disabled={!canProceed()}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors
                ${canProceed() ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'}
              `}
            >
              Avanti
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <div />
          )}
        </div>
      </Card>
    </div>
  )
}
